import { useEffect, useState } from "react";
import {
  FaTicketAlt,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import TicketService from "../../../../services/Ticket";

const dataForm = {
  subject: "",
  message: "",
};

export default function CreateTicketModal({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState(dataForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setForm(dataForm);
      setError("");
      setLoading(false);
      return;
    }

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm({ ...form, [key]: value });
    setError("");
  };

const handleSubmit = async () => {
  if (!form.subject.trim()) {
    setError("Please enter a subject.");
    return;
  }

  if (form.message.trim().length < 10) {
    setError("Please describe your issue (at least 10 characters).");
    return;
  }

  try {
    setLoading(true);
    setError("");

    const res = await TicketService.createTicket({
      subject: form.subject.trim(),
      description: form.message.trim(),
    });

    onCreated?.(res?.data);
    onClose();
  } catch (err) {
    setError(
      err?.response?.data?.en_message ||
        "Something went wrong. Please try again."
    );
  } finally {
    setLoading(false);
  }
};

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
      />

      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 w-28 h-28 bg-gradient-to-br from-orange-400/20 to-transparent rounded-br-full pointer-events-none" />

        <div className="relative px-5 pt-5 pb-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-md">
              <FaTicketAlt className="text-white" />
            </div>

            <div>
              <h2 className="font-bold text-gray-900 text-base">
                New Ticket
              </h2>
              <p className="text-xs text-gray-500">
                Our team replies within 24 hours
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={loading}
            className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <FaXmark className="text-gray-600" />
          </button>
        </div>

        
            <div className="relative px-5 py-5 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Subject
                </label>

                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) =>
                    handleChange("subject", e.target.value)
                  }
                  placeholder="e.g. Payment not showing in wallet"
                  maxLength={100}
                  className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 transition"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-700">
                    Description
                  </label>

                  <span className="text-[11px] text-gray-400">
                    {form.message.length}/1000
                  </span>
                </div>

                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) =>
                    handleChange("message", e.target.value)
                  }
                  maxLength={1000}
                  placeholder="Tell us what happened and what you expected..."
                  className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 outline-none resize-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 transition"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 text-red-600 text-xs rounded-xl px-3 py-2.5">
                  <FaExclamationCircle className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="relative px-5 py-4 border-t border-gray-100 bg-white flex gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-3 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-[2] bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <FaPaperPlane className="text-xs" />
                )}

                <span>{loading ? "Sending..." : "Submit Ticket"}</span>
              </button>
            </div>
      </div>
    </div>
  );
}
